import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import QuickPaymentPanel from "../QuickPaymentPanel";
import PosPaymentStep from "../PosPaymentStep";
import { useAuth } from "../../auth/AuthContext";
import { getSettings } from "../../services/settings";
import AddSaleDropdown from "./AddSaleDropdown";
import { canCreateBooking } from "../../lib/permissions";

interface DashboardHeaderProps {
  onQuickAction: (action: string) => void;
  onDateChange: (startDate: string, endDate: string) => void;
  currencySymbol?: string;
  title?: string;
  showRangeSelector?: boolean;
}

const DashboardHeader = ({
  onQuickAction,
  onDateChange,
  currencySymbol = "",
  title = "Dashboard Overview",
  showRangeSelector = true,
}: DashboardHeaderProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const isClaimed = user?.company?.is_claim === "yes";
  const [showQuickPaymentPanel, setShowQuickPaymentPanel] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"quick" | "paynow" | null>(null);
  const [quickPaymentType, setQuickPaymentType] = useState<"direct" | "stylist">("stylist");

  useEffect(() => {
    const fetchQuickPaymentType = async () => {
      try {
        const response = await getSettings();
        if (response.success && response.data?.settings) {
          const paymentType = response.data.settings.quick_payment_type;
          if (paymentType === "stylist" || paymentType === "active") {
            setQuickPaymentType("stylist");
          } else if (paymentType === "direct" || paymentType === "inactive") {
            setQuickPaymentType("direct");
          } else {
            setQuickPaymentType("stylist");
          }
        }
      } catch (error) {
        console.error("Failed to fetch quick payment type:", error);
        setQuickPaymentType("stylist");
      }
    };

    fetchQuickPaymentType();
    window.addEventListener("settingsUpdated", fetchQuickPaymentType);
    return () => window.removeEventListener("settingsUpdated", fetchQuickPaymentType);
  }, [location.pathname]);

  const handleRangeChange = (value: string) => {
    const endDate = new Date();
    let startDate = new Date();

    switch (value) {
      case "Today":
        break;
      case "This Week":
        startDate.setDate(endDate.getDate() - endDate.getDay());
        break;
      case "This Month":
        startDate.setDate(1);
        break;
      default:
        startDate.setDate(endDate.getDate() - endDate.getDay());
    }

    onDateChange(
      startDate.toISOString().split("T")[0],
      endDate.toISOString().split("T")[0]
    );
  };

  const handleAddSaleAction = (type: "Services" | "Sunbeds") => {
    if (type === "Services") onQuickAction("Add Sale");
    else if (type === "Sunbeds") onQuickAction("Add Sunbed Sale");
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 py-2 sm:py-0">
      <div className="flex items-center justify-between w-full lg:w-auto gap-2">
        <h1 className="text-[18px] sm:text-2xl md:text-3xl font-bold text-[#402B69] whitespace-nowrap">
          {title}
        </h1>

        {showRangeSelector && (
          <Select onValueChange={handleRangeChange} defaultValue="This Week">
            <SelectTrigger className="w-[130px] sm:w-40 bg-white shadow-sm border-gray-100 focus:ring-[#885ABB]/20 h-9 sm:h-10 text-xs sm:text-sm">
              <SelectValue placeholder="Select Range" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="Today">Today</SelectItem>
              <SelectItem value="This Week">This Week</SelectItem>
              <SelectItem value="This Month">This Month</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-3 min-w-fit">
        {isClaimed && quickPaymentType === "stylist" && (
          <button
            onClick={() => setShowQuickPaymentPanel(true)}
            className="whitespace-nowrap bg-[#22C55E] hover:bg-[#16a34a] text-white rounded-lg py-3 px-4 sm:px-6 font-bold flex items-center justify-center gap-2 transition shadow-sm text-xs sm:text-sm"
          >
            <img src="/quick.svg" alt="Quick Payment" className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Quick Payment</span>
          </button>
        )}

        {isClaimed && canCreateBooking(user) && (
          <AddSaleDropdown onAction={handleAddSaleAction} />
        )}
      </div>

      {showQuickPaymentPanel && (
        <QuickPaymentPanel
          onClose={() => setShowQuickPaymentPanel(false)}
          onProceed={() => {
            setShowQuickPaymentPanel(false);
            setSidebarMode("quick");
          }}
          quickPaymentType={quickPaymentType}
        />
      )}

      {sidebarMode && (
        <PosPaymentStep
          selectedService={{
            id: "service-1",
            name: "Hair Cut",
            duration: "60 mins",
            price: `${currencySymbol}35`,
          }}
          selectedCustomer="Karen Davies"
          appointmentTime="9.30–10.30 am"
          tip={0}
          setTip={() => {}}
          goToFinalPayment={() => setSidebarMode(null)}
          onClose={() => setSidebarMode(null)}
          mode={sidebarMode}
        />
      )}
    </div>
  );
};

export default DashboardHeader;
